/** @file Bone form model: each transform channel's shown value, key marker, editability, and its edit call. */
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import { channelValueAt, type TransformChannel } from './channelValueAt';
import { TRANSFORM_CHANNELS } from './transformChannels.data';

/** Mode and playhead the form edits in. */
export interface BoneEditContext {
  mode: 'setup' | 'animate';
  animation: Id | null;
  frame: number;
}

/** One channel row: shown value, whether a key sits on this frame (or elsewhere), whether it can be edited. */
export interface ChannelRow {
  channel: TransformChannel;
  value: number;
  key: 'here' | 'other' | 'none';
  editable: boolean;
}

/** Bone form model functions. */
export const boneFormModel = {
  /** Setup values in Setup mode; values at the playhead (with key markers) in Animate mode with an animation. */
  rows(doc: Doc, bone: Id, ctx: BoneEditContext): ChannelRow[] {
    const user = doc.bones[bone]?.kind === undefined;
    const anim = ctx.mode === 'animate' ? ctx.animation : null;
    return TRANSFORM_CHANNELS.map(({ channel }): ChannelRow => {
      if (anim === null) return { channel, value: doc.bones[bone]?.setup[channel] ?? 0, key: 'none', editable: user && ctx.mode === 'setup' };
      const keys = doc.animations[anim]?.bones[bone]?.[channel] ?? [];
      const key = keys.some((k) => k.f === ctx.frame) ? 'here' : keys.length > 0 ? 'other' : 'none';
      return { channel, value: channelValueAt(doc, anim, bone, channel, ctx.frame), key, editable: user };
    });
  },
  /** bone.setTransform in Setup mode, animation.setKey at the playhead (absolute value) in Animate mode. */
  editCall(bone: Id, channel: TransformChannel, value: number, ctx: BoneEditContext): CommandCall {
    if (ctx.mode === 'setup' || ctx.animation === null) return { name: 'bone.setTransform', input: { target: bone, value: { [channel]: value } } };
    return { name: 'animation.setKey', input: { animation: ctx.animation, target: bone, channel, frame: ctx.frame, value } };
  },
};
